import type {
	SubjectTrackView,
	TrackHop,
	TrackSighting,
} from "@sitescene/types";

import {
	SubjectTrackOrigin,
	SubjectTrackStatus,
	TrackDirection,
	TrackHopState,
	TrackletModality,
} from "@sitescene/types";
import { useState } from "react";
import { Link, useParams } from "react-router";

import { DetailPanel, KeyValueList } from "@/components/detail-panel";
import { StatusMessage } from "@/components/status-message";
import { Badge } from "@/components/ui/badges/badges";
import { Button } from "@/components/ui/buttons/button";
import { Checkbox } from "@/components/ui/checkbox/checkbox";
import { eventPath } from "@/constants/routes";
import {
	DECIDED_BY,
	formatClock,
	formatScore,
	formatSpan,
	HOP_STATE,
	secondsBetween,
	TRACK_STATUS,
} from "@/features/tracking/format";
import { SightingPlayer } from "@/features/tracking/sighting-player";
import { TrackMap } from "@/features/tracking/track-map";
import {
	isTrackClosed,
	useTrack,
	useTrackActions,
} from "@/features/tracking/use-tracks";
import { formatWhen } from "@/pages/plausibility/format";
import { ReviewMode } from "@/pages/tracks/ReviewMode";
import { cx } from "@/utils/cx";

/** A row on the timeline: the seed, or a hop with its sighting. */
interface Entry {
	hop?: TrackHop;
	sighting: TrackSighting;
}

/**
 * Sightings on one camera with no gap longer than `VISIT_GAP_SECONDS`
 * between them: one walk past it. The tracker records a person once per
 * clip and again after each time they're hidden, so a visit is usually
 * several sightings.
 */
interface Visit {
	end: Date;
	entries: Entry[];
	/** Its first sighting's tracklet. */
	id: string;
	sensor: TrackSighting["sensor"];
	start: Date;
}

/** Longest gap between sightings on one camera that's still one visit. */
const VISIT_GAP_SECONDS = 30;

/**
 * One cross-camera track: its progress, the seed picker while it waits on
 * one, the sightings waiting for a decision, the timeline of visits to each
 * camera around the seed (each sighting playable with the person boxed and
 * open to confirm or reject), and the path on a map of the site's cameras.
 * Staff see the scores and the verifier's reasons.
 */
export const TrackDetail = () => {
	const { trackId } = useParams();
	const track = useTrack(trackId);
	const [selected, setSelected] = useState<string>();
	const [showRejected, setShowRejected] = useState(false);
	const [reviewing, setReviewing] = useState(false);
	// The camera under the pointer, on the timeline or the map
	const [hovered, setHovered] = useState<string>();

	if (!track.data || !trackId) {
		return (
			<StatusMessage
				error={track.error}
				isLoading={track.isLoading}
				loading="Loading the track…"
			/>
		);
	}

	const view = track.data;
	const entries = timeline(view, showRejected);
	const visits = toVisits(entries);
	// The path: visits without rejected sightings, numbered as on the map
	const path = toVisits(timeline(view, false));
	const numbers = new Map(path.map((visit, index) => [visit.id, index + 1]));
	const rejectedCount = view.hops.filter(
		(hop) => hop.state === TrackHopState.Rejected,
	).length;
	const checking = new Set(view.expansionProgress?.checking ?? []);
	const waiting = view.hops.filter(
		(hop) =>
			hop.state === TrackHopState.NeedsReview && !checking.has(hop.tracklet),
	).length;
	const current =
		entries.find((entry) => entry.sighting.tracklet === selected) ??
		entries.find((entry) => !entry.hop) ??
		entries[0];

	return (
		<div className="flex flex-col gap-4">
			<TrackSummary track={view} trackId={trackId} />

			{view.status === SubjectTrackStatus.AwaitingSeed && (
				<SeedPicker candidates={view.seedCandidates ?? []} trackId={trackId} />
			)}

			<Following checking={checking.size} track={view} />

			{!reviewing && waiting > 0 && !isTrackClosed(view) && (
				<div className="border-warning-300 bg-warning-50 flex flex-wrap items-center justify-between gap-3 rounded-md border px-4 py-3 text-sm">
					<p className="text-warning-700">
						<span className="font-semibold">
							{waiting} sighting{waiting === 1 ? " needs" : "s need"} your
							decision.
						</span>{" "}
						Gemini couldn't tell whether {waiting === 1 ? "it's" : "they're"}{" "}
						the same person.
					</p>
					<Button onClick={() => setReviewing(true)} size="sm">
						Review {waiting === 1 ? "it" : "them"}
					</Button>
				</div>
			)}

			{reviewing && (
				<ReviewMode onClose={() => setReviewing(false)} track={view} />
			)}

			{!reviewing && entries.length > 0 && (
				<div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
					<section className="flex min-w-0 flex-col gap-2">
						<div className="flex flex-wrap items-center justify-between gap-3">
							<h3 className="text-sm font-semibold text-primary">
								Timeline: {visits.length} visit{visits.length === 1 ? "" : "s"},{" "}
								{entries.length} sighting{entries.length === 1 ? "" : "s"}
							</h3>
							{rejectedCount > 0 && (
								<Checkbox
									isSelected={showRejected}
									label={`Show ${rejectedCount} rejected`}
									onChange={setShowRejected}
								/>
							)}
						</div>
						<ol className="flex flex-col">
							{visits.map((visit, index) => {
								const previous = visits[index - 1];
								return (
									<li key={visit.id}>
										{previous && (
											<p className="py-1 pl-24 text-xs text-tertiary">
												{gapText(previous, visit)}
											</p>
										)}
										<VisitRow
											checking={checking}
											closed={isTrackClosed(view)}
											isHighlighted={hovered === visit.sensor.id}
											number={numbers.get(visit.id)}
											onHover={setHovered}
											onSelect={setSelected}
											selected={current?.sighting.tracklet}
											trackId={trackId}
											visit={visit}
										/>
									</li>
								);
							})}
						</ol>
					</section>

					{current && (
						<section className="flex min-w-0 flex-col gap-2 xl:sticky xl:top-4 xl:self-start">
							<h3 className="text-sm font-semibold text-primary">
								{current.sighting.sensor.name} ·{" "}
								{formatClock(current.sighting.startTimestamp)}
							</h3>
							<SightingPlayer
								key={current.sighting.tracklet}
								sighting={current.sighting}
							/>
							<div className="flex flex-wrap gap-2">
								{current.sighting.cropUrls.map((url) => (
									<img
										alt=""
										className="h-28 rounded border border-secondary object-contain"
										key={url}
										src={url}
									/>
								))}
							</div>
							<h3 className="pt-2 text-sm font-semibold text-primary">Path</h3>
							{!view.topologyConfigured && (
								<p className="text-xs text-tertiary">
									Camera links aren't set up for this site, so walking times
									between cameras are estimated from their distance on the map.
								</p>
							)}
							<TrackMap
								highlighted={hovered}
								onHover={setHovered}
								onSelect={(sensorId) => {
									const first = entries.find(
										(entry) => entry.sighting.sensor.id === sensorId,
									);
									if (first) setSelected(first.sighting.tracklet);
								}}
								sensors={view.sensors}
								stops={path.map((visit) => visit.sensor.id)}
							/>
						</section>
					)}
				</div>
			)}
		</div>
	);
};

const TrackSummary = ({
	track,
	trackId,
}: {
	track: SubjectTrackView;
	trackId: string;
}) => {
	const { dismiss } = useTrackActions(trackId);
	const status = TRACK_STATUS[track.status];
	const cameras = new Set(
		Object.values(track.sightings).map((sighting) => sighting.sensor.id),
	);
	const count = (state: TrackHopState) =>
		track.hops.filter((hop) => hop.state === state).length;
	const indexed = track.indexedClips.length;

	return (
		<DetailPanel title="Track">
			<div className="flex flex-col gap-3">
				<KeyValueList
					items={[
						{
							label: "Status",
							value: (
								<Badge
									color={status.tone}
									label={status.label}
									value={track.status}
								/>
							),
						},
						{
							label: "Started from",
							value: (
								<span>
									<Link
										className="underline"
										to={eventPath(String(track.event))}
									>
										The event
									</Link>
									{track.origin === SubjectTrackOrigin.Auto
										? ", automatically"
										: ", by an operator"}
								</span>
							),
						},
						...(track.seedPick
							? [
									{
										label: "Who Gemini picked",
										value: (
											<span className="flex flex-col gap-1">
												{track.subjectUnconfirmed && (
													<span>
														<Badge
															color="warning"
															label="Unsure: check the person"
															value="unconfirmed"
														/>
													</span>
												)}
												<span>
													{track.seedPick.reason || "No reason given"}
												</span>
											</span>
										),
									},
								]
							: []),
						{
							label: "Window",
							value: `${formatWhen(track.windowStart)} to ${formatClock(track.windowEnd)} (±${track.horizonMinutes} min)`,
						},
						{
							label: "Clips checked",
							value: `${indexed} of ${track.clips.length}`,
						},
						{
							label: "Sightings",
							value: `${track.hops.length + track.seedTracklets.length} on ${cameras.size} camera${cameras.size === 1 ? "" : "s"}: ${count(TrackHopState.Auto)} auto, ${count(TrackHopState.Confirmed)} confirmed, ${count(TrackHopState.NeedsReview)} need review, ${count(TrackHopState.Rejected)} rejected`,
						},
						...(track.failureReason
							? [{ label: "Why it failed", value: track.failureReason }]
							: []),
					]}
				/>
				<div
					aria-hidden="true"
					className="h-1.5 overflow-hidden rounded-full bg-gray-200"
				>
					<div
						className="h-full bg-brand-500 transition-[width]"
						style={{
							width: `${track.clips.length ? (indexed / track.clips.length) * 100 : 0}%`,
						}}
					/>
				</div>
				{!isTrackClosed(track) && (
					<div className="flex flex-wrap gap-2">
						<Button
							color="secondary"
							isDisabled={dismiss.isPending}
							onClick={() => dismiss.mutate()}
							size="sm"
						>
							Dismiss track
						</Button>
					</div>
				)}
			</div>
		</DetailPanel>
	);
};

/**
 * While the track is finding where the person went: what it's doing, so the
 * page isn't silent while Gemini checks candidates.
 */
const Following = ({
	checking,
	track,
}: {
	checking: number;
	track: SubjectTrackView;
}) => {
	const following =
		track.status === SubjectTrackStatus.Expanding &&
		track.seedTracklets.length > 0;
	const recomputing =
		!isTrackClosed(track) &&
		track.seedTracklets.length > 0 &&
		(track.expandedRevision ?? -1) < track.revision;
	if (!checking && !following && !recomputing) return null;

	const indexing = track.indexedClips.length < track.clips.length;
	return (
		<div className="flex items-center gap-3 rounded-md border border-brand-200 bg-brand-50 px-4 py-3 text-sm text-brand-700">
			<span
				aria-hidden="true"
				className="size-2.5 shrink-0 animate-pulse rounded-full bg-brand-500"
			/>
			<p>
				<span className="font-semibold">Finding where they went…</span>{" "}
				{checking > 0
					? `Gemini is checking ${checking} possible sighting${checking === 1 ? "" : "s"}. They show below as it goes.`
					: indexing
						? "Clips are still being checked; sightings appear as they're found."
						: "Matching people across the cameras."}
			</p>
		</div>
	);
};

/**
 * One visit to a camera: its first crop, number on the map, times, and how
 * its sightings stand. Opens to the sightings, each playable and open to a
 * decision.
 */
const VisitRow = ({
	checking,
	closed,
	isHighlighted,
	number,
	onHover,
	onSelect,
	selected,
	trackId,
	visit,
}: {
	checking: ReadonlySet<string>;
	closed: boolean;
	isHighlighted: boolean;
	number?: number;
	onHover: (sensorId: string | undefined) => void;
	onSelect: (tracklet: string) => void;
	selected?: string;
	trackId: string;
	visit: Visit;
}) => {
	const [open, setOpen] = useState(false);
	const seed = visit.entries.find((entry) => !entry.hop);
	const lead = seed ?? visit.entries[0];
	const tally = (predicate: (entry: Entry) => boolean) =>
		visit.entries.filter(predicate).length;
	const isChecking = (entry: Entry) =>
		Boolean(entry.hop && checking.has(entry.hop.tracklet));
	const parts = [
		[tally((entry) => isChecking(entry)), "Gemini checking"],
		[
			tally(
				(entry) =>
					entry.hop?.state === TrackHopState.NeedsReview && !isChecking(entry),
			),
			"need review",
		],
		[
			tally((entry) => entry.hop?.state === TrackHopState.Confirmed),
			"confirmed",
		],
		[tally((entry) => entry.hop?.state === TrackHopState.Auto), "auto"],
		[tally((entry) => entry.hop?.state === TrackHopState.Rejected), "rejected"],
	].filter(([count]) => Number(count) > 0) as [number, string][];
	const isSelected = visit.entries.some(
		(entry) => entry.sighting.tracklet === selected,
	);
	if (!lead) return null;

	return (
		<div
			className={cx(
				"flex flex-col gap-2 rounded-md border p-2",
				isSelected ? "border-brand" : "border-secondary",
				isHighlighted && "bg-secondary",
			)}
			onMouseEnter={() => onHover(visit.sensor.id)}
			onMouseLeave={() => onHover(undefined)}
		>
			<div className="flex gap-3">
				<button
					aria-label={`Play ${visit.sensor.name} at ${formatClock(visit.start)}`}
					className="shrink-0 rounded focus-visible:outline-2 focus-visible:outline-brand"
					onClick={() => onSelect(lead.sighting.tracklet)}
					type="button"
				>
					{lead.sighting.cropUrls[0] ? (
						<img
							alt=""
							className="h-24 w-20 rounded bg-gray-900 object-contain"
							src={lead.sighting.cropUrls[0]}
						/>
					) : (
						<span className="block h-24 w-20 rounded bg-gray-200" />
					)}
				</button>
				<div className="flex min-w-0 flex-1 flex-col gap-1 text-sm">
					<button
						className="flex flex-wrap items-center gap-2 text-left"
						onClick={() => onSelect(lead.sighting.tracklet)}
						type="button"
					>
						{number !== undefined && (
							<span className="flex size-5 items-center justify-center rounded-full bg-brand-600 text-xs font-semibold text-white">
								{number}
							</span>
						)}
						<span className="font-medium text-primary">
							{visit.sensor.name}
						</span>
						<span className="text-tertiary">
							{formatClock(visit.start)}–{formatClock(visit.end)} ·{" "}
							{formatSpan(secondsBetween(visit.start, visit.end))}
						</span>
						{seed && <Badge color="brand" label="The event" value="seed" />}
					</button>
					<p className="text-xs text-secondary">
						{visit.entries.length} sighting
						{visit.entries.length === 1 ? "" : "s"}
						{parts.length > 0 &&
							`: ${parts.map(([count, label]) => `${count} ${label}`).join(", ")}`}
					</p>
					<div>
						<Button
							color="link-gray"
							onClick={() => setOpen((current) => !current)}
							size="xs"
						>
							{open ? "Hide sightings" : "Show sightings"}
						</Button>
					</div>
				</div>
			</div>
			{open && (
				<ol className="flex flex-col gap-2 border-l-2 border-secondary pl-3">
					{visit.entries.map((entry) => (
						<li key={entry.sighting.tracklet}>
							<TimelineRow
								checking={isChecking(entry)}
								closed={closed}
								entry={entry}
								isSelected={entry.sighting.tracklet === selected}
								onSelect={() => onSelect(entry.sighting.tracklet)}
								trackId={trackId}
							/>
						</li>
					))}
				</ol>
			)}
		</div>
	);
};

/** The people in the event's clips; picking one starts the track from them. */
const SeedPicker = ({
	candidates,
	trackId,
}: {
	candidates: TrackSighting[];
	trackId: string;
}) => {
	const { setSeed } = useTrackActions(trackId);

	return (
		<DetailPanel title="Who should be followed?">
			<p className="mb-3 text-sm text-tertiary">
				The event's clips show more than one person. Pick the one to follow.
			</p>
			<div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
				{candidates.map((candidate) => (
					<button
						className="flex flex-col gap-1 rounded-md border border-secondary p-2 text-left hover:border-brand focus-visible:outline-2 focus-visible:outline-brand disabled:opacity-50"
						disabled={setSeed.isPending}
						key={candidate.tracklet}
						onClick={() => setSeed.mutate(candidate.tracklet)}
						type="button"
					>
						{candidate.cropUrls[0] && (
							<img
								alt=""
								className="h-36 w-full rounded object-contain"
								src={candidate.cropUrls[0]}
							/>
						)}
						<span className="text-sm font-medium text-primary">
							{candidate.sensor.name}
						</span>
						<span className="text-xs text-tertiary">
							{formatClock(candidate.startTimestamp)} ·{" "}
							{formatSpan(
								secondsBetween(
									candidate.startTimestamp,
									candidate.endTimestamp,
								),
							)}
						</span>
					</button>
				))}
			</div>
			{setSeed.error && (
				<p className="mt-2 text-sm text-error-primary" role="alert">
					{setSeed.error.message}
				</p>
			)}
		</DetailPanel>
	);
};

const TimelineRow = ({
	checking,
	closed,
	entry,
	isSelected,
	onSelect,
	trackId,
}: {
	/** Gemini is looking at it now. */
	checking: boolean;
	closed: boolean;
	entry: Entry;
	isSelected: boolean;
	onSelect: () => void;
	trackId: string;
}) => {
	const { review } = useTrackActions(trackId);
	const { hop, sighting } = entry;
	const state = hop ? HOP_STATE[hop.state] : undefined;
	// Each row has its own mutation, so its pending and error are its own
	const deciding = review.isPending;

	return (
		<div
			className={cx(
				"flex gap-3 rounded-md border p-2",
				isSelected ? "border-brand" : "border-secondary",
				hop?.state === TrackHopState.Rejected && "opacity-60",
			)}
		>
			<button
				aria-label={`Play ${sighting.sensor.name} at ${formatClock(sighting.startTimestamp)}`}
				className="shrink-0 rounded focus-visible:outline-2 focus-visible:outline-brand"
				onClick={onSelect}
				type="button"
			>
				{sighting.cropUrls[0] ? (
					<img
						alt=""
						className="h-24 w-20 rounded bg-gray-900 object-contain"
						src={sighting.cropUrls[0]}
					/>
				) : (
					<span className="block h-24 w-20 rounded bg-gray-200" />
				)}
			</button>
			<div className="flex min-w-0 flex-1 flex-col gap-1 text-sm">
				<button
					className="flex flex-wrap items-center gap-2 text-left"
					onClick={onSelect}
					type="button"
				>
					<span className="font-medium text-primary">
						{sighting.sensor.name}
					</span>
					<span className="text-tertiary">
						{formatClock(sighting.startTimestamp)} ·{" "}
						{formatSpan(
							secondsBetween(sighting.startTimestamp, sighting.endTimestamp),
						)}
					</span>
				</button>
				<div className="flex flex-wrap items-center gap-2">
					{hop && state ? (
						<>
							{checking ? (
								<Badge
									color="brand"
									label="Gemini checking…"
									value="checking"
								/>
							) : (
								<Badge
									color={state.tone}
									label={state.label}
									value={hop.state}
								/>
							)}
							<span className="text-xs text-tertiary">
								{hop.direction === TrackDirection.Backward ? "Before" : "After"}{" "}
								the event
								{hop.decidedBy ? ` · ${DECIDED_BY[hop.decidedBy]}` : ""}
								{sighting.modality === TrackletModality.Grayscale
									? " · grayscale"
									: ""}
							</span>
						</>
					) : (
						<Badge color="brand" label="The event" value="seed" />
					)}
				</div>
				{hop && (
					<p className="text-xs text-tertiary tabular-nums">
						{hop.score.crossModality
							? "Joined on timing across color and grayscale"
							: `Appearance ${formatScore(hop.score.appearance)} · topology ${formatScore(hop.score.topology)} · margin ${formatScore(hop.score.margin)} · combined ${formatScore(hop.score.combined)}`}
					</p>
				)}
				{hop?.score.verifier && (
					<p className="text-xs text-secondary">
						Gemini:{" "}
						<span className="font-medium">{hop.score.verifier.answer}</span>.{" "}
						{hop.score.verifier.reason}
					</p>
				)}
				{hop && !closed && (
					<div className="flex flex-wrap gap-2 pt-1">
						<Button
							color={
								hop.state === TrackHopState.NeedsReview
									? "primary"
									: "secondary"
							}
							isDisabled={deciding || hop.state === TrackHopState.Confirmed}
							onClick={() =>
								review.mutate({ confirmed: true, tracklet: sighting.tracklet })
							}
							size="sm"
						>
							Same person
						</Button>
						<Button
							color="secondary"
							isDisabled={deciding || hop.state === TrackHopState.Rejected}
							onClick={() =>
								review.mutate({ confirmed: false, tracklet: sighting.tracklet })
							}
							size="sm"
						>
							Not them
						</Button>
					</div>
				)}
				{review.error && (
					<p className="text-xs text-error-primary" role="alert">
						{review.error.message}
					</p>
				)}
			</div>
		</div>
	);
};

/** The seed and every hop in time order, rejected ones left out unless asked for. */
const timeline = (track: SubjectTrackView, showRejected: boolean): Entry[] => {
	const seeds: Entry[] = track.seedTracklets.flatMap((id) => {
		const sighting = track.sightings[id];
		return sighting ? [{ sighting }] : [];
	});
	const hops: Entry[] = track.hops
		.filter((hop) => showRejected || hop.state !== TrackHopState.Rejected)
		.flatMap((hop) => {
			const sighting = track.sightings[hop.tracklet];
			return sighting ? [{ hop, sighting }] : [];
		});
	return [...seeds, ...hops].sort(
		(a, b) =>
			new Date(a.sighting.startTimestamp).getTime() -
			new Date(b.sighting.startTimestamp).getTime(),
	);
};

/**
 * Entries grouped into visits: per camera, a new visit after a gap longer
 * than `VISIT_GAP_SECONDS`; then in order of when each visit starts. Two
 * cameras with overlapping views give visits that overlap in time.
 */
const toVisits = (entries: Entry[]): Visit[] => {
	const open = new Map<string, Visit>();
	const visits: Visit[] = [];
	for (const entry of entries) {
		const { sighting } = entry;
		const start = new Date(sighting.startTimestamp);
		const end = new Date(sighting.endTimestamp);
		const visit = open.get(sighting.sensor.id);
		if (visit && secondsBetween(visit.end, start) <= VISIT_GAP_SECONDS) {
			visit.entries.push(entry);
			if (end > visit.end) visit.end = end;
			continue;
		}
		const next: Visit = {
			end,
			entries: [entry],
			id: sighting.tracklet,
			sensor: sighting.sensor,
			start,
		};
		open.set(sighting.sensor.id, next);
		visits.push(next);
	}
	return visits;
};

/** What happened between two visits in time order. */
const gapText = (previous: Visit, next: Visit): string => {
	const gap = secondsBetween(previous.end, next.start);
	if (gap < 0) return `seen on both at once for ${formatSpan(-gap)}`;
	return previous.sensor.id === next.sensor.id
		? `${formatSpan(gap)} later, same camera`
		: `${formatSpan(gap)} later`;
};
